import React, { useState } from "react"
import { Button } from "@chakra-ui/react"
import { useFlags } from "launchdarkly-react-client-sdk"
import LaunchModal from "./LaunchModal/LaunchModal"
import { LaunchModalContentType } from "./utils/launchModalUtils"
import { useRelatedCompanies, useLaunchCarrotCard } from "./hooks/launch"

interface LaunchButtonProps {
  companyId: number
  stripeAccountId: string
  onLaunchSuccess: (companies: { id: number; name: string }[]) => void
  onLaunchError: () => void
  onGetCompanysError: () => void
}

export default function LaunchInitiation({
  companyId,
  stripeAccountId,
  onLaunchSuccess,
  onLaunchError,
  onGetCompanysError
}: LaunchButtonProps): JSX.Element {
  const { ccobLaunchButton } = useFlags()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<LaunchModalContentType | null>(null)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<{ id: number; countryCode: string; name: string }[]>([])
  const [billingAddress, setBillingAddress] = useState(null)
  const [launchSuccessful, setLaunchSuccessful] = useState(false)

  const relatedCompanies = useRelatedCompanies(companyId)
  const launchMutation = useLaunchCarrotCard(stripeAccountId)

  const segmentOptions = relatedCompanies.data?.map((company) => ({
    id: company.id,
    countryCode: company.countryCode,
    name: company.name
  }))

  const openModal = async () => {
    if (relatedCompanies.isError) {
      onGetCompanysError()
      return null
    }

    if (segmentOptions) {
      if (segmentOptions.length === 1) {
        setSelectedCompanyIds(segmentOptions)
        setModalContent(LaunchModalContentType.SetStripeBalances)
      } else {
        setModalContent(LaunchModalContentType.SelectSegments)
      }
      setIsModalOpen(true)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setModalContent(null)
    setSelectedCompanyIds([])
    setBillingAddress(null)
  }

  const handleSegmentsSubmit = (
    selectedIds: { id: number; countryCode: string; name: string }[],
    hasInternationalSegment: boolean
  ) => {
    setSelectedCompanyIds(selectedIds)
    if (hasInternationalSegment) {
      setModalContent(LaunchModalContentType.AddBillingAddress)
    } else {
      setModalContent(LaunchModalContentType.SetStripeBalances)
    }
  }

  const handleBillingSubmit = (billingAddress) => {
    setBillingAddress(billingAddress)
    setModalContent(LaunchModalContentType.SetStripeBalances)
  }

  const handleLaunchSubmit = async (accountDetails) => {
    const payload = {
      companyIds: selectedCompanyIds.map((item) => item.id),
      address: billingAddress,
      accountDetails: {
        minimumBalance: parseInt(accountDetails.stripeMinimum),
        targetBalance: parseInt(accountDetails.stripeTarget)
      }
    }

    try {
      await launchMutation.mutateAsync(payload)
      onLaunchSuccess(selectedCompanyIds.map((item) => ({ id: item.id, name: item.name })))
      setLaunchSuccessful(true)
    } catch (error) {
      onLaunchError()
    } finally {
      closeModal()
    }
  }

  return (
    <>
      {ccobLaunchButton && !launchSuccessful && (
        <Button variant="outline" mt="45px" onClick={openModal}>
          Initiate Carrot Card Launch
        </Button>
      )}
      <LaunchModal
        isOpen={isModalOpen}
        onClose={closeModal}
        content={modalContent}
        onSubmit={
          modalContent === LaunchModalContentType.SetStripeBalances
            ? handleLaunchSubmit
            : modalContent === LaunchModalContentType.AddBillingAddress
            ? handleBillingSubmit
            : handleSegmentsSubmit
        }
        companyIds={segmentOptions}
      />
    </>
  )
}
